import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './styles/App.css';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import RegisterUser from './pages/RegisterUser';
import EditUser from './pages/EditUser';
import ChangePassword from './pages/ChangePassword';
import SyndromeCharacteristics from './pages/SyndromeCharacteristics';
// import CreateCharacteristic from './pages/CreateCharacteristic';
// import EditCharacteristic from './pages/EditCharacteristic';
import Professions from './pages/Professions';
import EspecialidadMedicoManagement from './pages/EspecialidadMedicoManagement';
import RegistrarEspecialidadMedico from './pages/RegistrarEspecialidadMedico';
import EditarEspecialidadMedico from './pages/EditarEspecialidadMedico';
import CreateProfession from './pages/CreateProfession';
import EditProfession from './pages/EditProfession';
import DoctorManagement from './pages/DoctorManagement';
import MentorManagement from './pages/MentorManagement';
import HospitalManagement from './pages/HospitalManagement';
import TipoSociaManagement from './pages/TipoSociaManagement';
import TallaManagement from './pages/TallaManagement';
import InventoryManagement from './pages/InventoryManagement';
import UbicacionInventarioManagement from './pages/UbicacionInventarioManagement';
import CategoriaInventarioManagement from './pages/CategoriaInventarioManagement';
import RegisterPartner from './pages/RegisterPartner';
import EditPartner from './pages/EditPartner';
import PartnersList from './pages/PartnersList';
import RegisterPartnerSummary from './pages/RegisterPartnerSummary';
import EditarFormatos from './pages/EditarFormatos';
import Reports from './pages/Reports';
import RequisitosSociaManagement from './pages/RequisitosSociaManagement';
import TratamientoManagement from './pages/TratamientoManagement';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import logoFepast from './assets/images/Logo.png';
import authService from './services/authService';
import sociasApiService from './services/sociasApiService';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState(null);
  // Inicializa el avatar desde localStorage si existe
  const [avatarUrl, setAvatarUrl] = React.useState(() => {
    return localStorage.getItem('avatarUrl') || null;
  });
  const [userIds, setUserIds] = React.useState({ sociaId: null });

  // Normaliza los datos de usuario a un shape común para el header
  const normalizeUser = (u) => {
    if (!u) return null;
    // Intentar obtener nombre legible desde diversas propiedades
    const name = (
      u.nombreCompleto ||
      u.NOMBRE_COMPLETO ||
      u.SOCIA_NOMBRE ||
      u.socia_nombre ||
      u.name ||
      u.fullName ||
      u.username ||
      'Usuario'
    );
    // Primer permiso como rol si existe
    let role = 'Usuario';
    if (Array.isArray(u.permisos) && u.permisos.length > 0) {
      const p = u.permisos[0];
      role = (p?.PERMISO_NOMBRE || p?.permiso_nombre || p?.nombre || p?.name || role);
    }
    const email = (
      u.SOCIA_EMAIL || u.socia_email || u.email || u.USUARIO_EMAIL || ''
    );
    const sociaId = u.sociaId || u.SOCIA_ID || u.socia_id || null;
    return { name, role, email, sociaId };
  };

  // Cargar datos del usuario desde ambos orígenes: authService (fepast_user_data) y 'currentUser'
  const refreshHeaderUser = React.useCallback(() => {
    try {
      const apiUser = authService.getCurrentUser();
      const legacyRaw = localStorage.getItem('currentUser');
      const legacyUser = legacyRaw ? JSON.parse(legacyRaw) : null;
      // Priorizar el usuario con más información de nombre/email
      const pick = apiUser || legacyUser;
      const normalized = normalizeUser(pick) || normalizeUser(legacyUser);
      setCurrentUser(normalized);
      const nextSociaId = pick?.sociaId || pick?.SOCIA_ID || pick?.socia_id || normalized?.sociaId || null;
      setUserIds({ sociaId: nextSociaId });
      // Reiniciar avatar solo si el usuario cambió
      setAvatarUrl(localStorage.getItem('avatarUrl') || null);

      // Intento inmediato de avatar si viene embebido en el usuario (ahora desde Cloudinary)
      try {
        const directUrl = pick?.sociaFotoUrl || pick?.SOCIA_FOTO_URL || null;
        if (directUrl) {
          setAvatarUrl(directUrl);
          localStorage.setItem('avatarUrl', directUrl);
        }
      } catch (e) {
        // Ignorar si no se puede obtener la URL
        if (process && process.env && process.env.NODE_ENV === 'development') {
          console.debug('Avatar directo no disponible');
        }
      }
    } catch (err) {
      console.warn('No se pudieron leer datos de usuario para el header');
      setCurrentUser(null);
      setUserIds({ sociaId: null });
    }
  }, []);

  React.useEffect(() => {
    refreshHeaderUser();
    // Re-evaluar al cambiar de ruta para capturar login/redirect
  }, [refreshHeaderUser, location.pathname]);

  // Cargar foto de la socia si tenemos sociaId y aún no hay avatarUrl
  React.useEffect(() => {
    let cancelled = false;
    const loadAvatar = async () => {
      if (!userIds.sociaId) {
        setAvatarUrl(null);
        localStorage.removeItem('avatarUrl');
        return;
      }
      try {
        const socia = await sociasApiService.getSociaById(userIds.sociaId);
        // Ahora la foto viene como URL de Cloudinary
        const fotoUrl = socia?.sociaFotoUrl || null;
        if (!cancelled) {
          setAvatarUrl(fotoUrl);
          if (fotoUrl) {
            localStorage.setItem('avatarUrl', fotoUrl);
          } else {
            localStorage.removeItem('avatarUrl');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setAvatarUrl(null);
          localStorage.removeItem('avatarUrl');
        }
      }
    };
    loadAvatar();
    return () => { cancelled = true; };
  }, [userIds.sociaId]);
  
  const handleLogout = () => {
    try {
      authService.logout();
      localStorage.removeItem('currentUser');
      localStorage.removeItem('avatarUrl');
      setCurrentUser(null);
      setUserIds({ sociaId: null });
      setAvatarUrl(null);
    } finally {
      navigate('/');
    }
  };
  
  const handleChangePassword = () => {
    navigate('/change-password');
  };
  
  // Ocultar botones de usuario en las páginas de login
  const isLoginPage = location.pathname === '/' || location.pathname === '/login';
  const showUserActions = !isLoginPage;
  
  return (
    <header className="App-header">
      <div className="header-content">
        <div className="header-left">
          <img src={logoFepast} alt="FEPAST Logo" className="header-logo" />
          <div className="header-text">
            <h1>FEPAST</h1>
          </div>
        </div>
        {showUserActions && (
          <div className="header-actions">
            <button className="change-password-button" onClick={handleChangePassword}>
              <i className="fas fa-key"></i>
              <span>Cambiar Contraseña</span>
            </button>
            <button className="logout-button" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              <span>Cerrar Sesión</span>
            </button>
            <div className="user-avatar" title="Usuario" onClick={() => setShowUserMenu(v => !v)}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="Usuario" />
              ) : (
                <i className="fas fa-user-circle"></i>
              )}
            </div>
            {showUserMenu && (
              <div
                className="user-menu"
                onMouseLeave={() => setShowUserMenu(false)}
                style={{
                  position: 'absolute',
                  top: '60px',
                  right: '30px',
                  minWidth: '240px',
                  background: '#fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  borderRadius: '14px',
                  zIndex: 1000,
                  padding: '1.2rem 1.4rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <div className="user-name" style={{ fontWeight: 600, fontSize: '1.15rem', color: '#b71c50', marginBottom: '6px', textAlign: 'center', letterSpacing: '0.5px' }}>{currentUser?.name || 'Usuario'}</div>
                  <div className="user-email" style={{ color: '#444', fontSize: '0.98rem', marginBottom: '2px', textAlign: 'center', letterSpacing: '0.2px' }}>{currentUser?.email || ''}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

function AppContent() {
  const location = useLocation();

  useEffect(() => {
    // Agregar clase al body basado en la ruta
    if (location.pathname === '/' || location.pathname === '/login') {
      document.body.classList.add('login-page');
    } else {
      document.body.classList.remove('login-page');
    }
  }, [location.pathname]);

  // No mostrar sidebar en las páginas de login, pero sí el header
  const isLoginPage = location.pathname === '/' || location.pathname === '/login';
  const showSidebar = !isLoginPage;

  return (
    <div className="App">
      {showSidebar && <Sidebar />}
      <Header />
      
      <main className={`app-content ${showSidebar ? 'with-sidebar' : ''}`}>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/* Rutas protegidas por permisos */}
          <Route path="/users" element={<ProtectedRoute requiredPermission="CRUD Usuarios"><UserManagement /></ProtectedRoute>} />
          <Route path="/user-management" element={<ProtectedRoute requiredPermission="CRUD Usuarios"><UserManagement /></ProtectedRoute>} />
          <Route path="/register-user" element={<ProtectedRoute requiredPermission="CRUD Usuarios"><RegisterUser /></ProtectedRoute>} />
          <Route path="/edit-user/:id?" element={<ProtectedRoute requiredPermission="CRUD Usuarios"><EditUser /></ProtectedRoute>} />
          <Route path="/create-user" element={<ProtectedRoute requiredPermission="CRUD Usuarios"><RegisterUser /></ProtectedRoute>} />

          <Route path="/syndrome-characteristics" element={<ProtectedRoute requiredPermission="CRUD Caracteristicas"><SyndromeCharacteristics /></ProtectedRoute>} />
          {/* <Route path="/syndrome-characteristics/create" element={<ProtectedRoute requiredPermission="CRUD Caracteristicas"><CreateCharacteristic /></ProtectedRoute>} /> */}
          {/* <Route path="/syndrome-characteristics/edit/:id" element={<ProtectedRoute requiredPermission="CRUD Caracteristicas"><EditCharacteristic /></ProtectedRoute>} /> */}

          <Route path="/professions" element={<ProtectedRoute requiredPermission="CRUD Profesiones"><Professions /></ProtectedRoute>} />
          <Route path="/professions/create" element={<ProtectedRoute requiredPermission="CRUD Profesiones"><CreateProfession /></ProtectedRoute>} />
          <Route path="/professions/edit/:id" element={<ProtectedRoute requiredPermission="CRUD Profesiones"><EditProfession /></ProtectedRoute>} />

          <Route path="/especialidades-medicas" element={<ProtectedRoute requiredPermission="CRUD Especialidad Médico"><EspecialidadMedicoManagement /></ProtectedRoute>} />
          <Route path="/especialidades-medicas/crear" element={<ProtectedRoute requiredPermission="CRUD Especialidad Médico"><RegistrarEspecialidadMedico /></ProtectedRoute>} />
          <Route path="/especialidades-medicas/editar/:id" element={<ProtectedRoute requiredPermission="CRUD Especialidad Médico"><EditarEspecialidadMedico /></ProtectedRoute>} />

          <Route path="/medicos" element={<ProtectedRoute requiredPermission="CRUD Medicos"><DoctorManagement /></ProtectedRoute>} />
          <Route path="/medicos/create" element={<ProtectedRoute requiredPermission="CRUD Medicos"><DoctorManagement createMode={true} /></ProtectedRoute>} />

          <Route path="/mentores" element={<ProtectedRoute requiredPermission="CRUD Mentores"><MentorManagement /></ProtectedRoute>} />
          <Route path="/mentores/create" element={<ProtectedRoute requiredPermission="CRUD Mentores"><MentorManagement createMode={true} /></ProtectedRoute>} />
          <Route path="/mentors" element={<ProtectedRoute requiredPermission="CRUD Mentores"><MentorManagement /></ProtectedRoute>} />
          <Route path="/mentors/create" element={<ProtectedRoute requiredPermission="CRUD Mentores"><MentorManagement createMode={true} /></ProtectedRoute>} />

          <Route path="/hospitals" element={<ProtectedRoute requiredPermission="CRUD Hospitales"><HospitalManagement /></ProtectedRoute>} />
          <Route path="/hospitals/create" element={<ProtectedRoute requiredPermission="CRUD Hospitales"><HospitalManagement createMode={true} /></ProtectedRoute>} />

          <Route path="/tipo-socia" element={<ProtectedRoute requiredPermission="CRUD Tipo Socia"><TipoSociaManagement /></ProtectedRoute>} />
          {/* <Route path="/tipo-socia/create" element={<ProtectedRoute requiredPermission="CRUD Tipo Socia"><TipoSociaManagement createMode={true} /></ProtectedRoute>} /> */}

          <Route path="/tallas" element={<ProtectedRoute requiredPermission="CRUD Talla"><TallaManagement /></ProtectedRoute>} />
          <Route path="/tallas/create" element={<ProtectedRoute requiredPermission="CRUD Talla"><TallaManagement createMode={true} /></ProtectedRoute>} />

          <Route path="/inventory" element={<ProtectedRoute requiredPermission="CRUD Inventario"><InventoryManagement /></ProtectedRoute>} />
          <Route path="/inventory/create" element={<ProtectedRoute requiredPermission="CRUD Inventario"><InventoryManagement createMode={true} /></ProtectedRoute>} />
          <Route path="/ubicacion-inventario" element={<ProtectedRoute requiredPermission="CRUD Ubicacion Inventario"><UbicacionInventarioManagement /></ProtectedRoute>} />
          <Route path="/inventory/location" element={<ProtectedRoute requiredPermission="CRUD Ubicacion Inventario"><UbicacionInventarioManagement /></ProtectedRoute>} />
          <Route path="/inventory/location/create" element={<ProtectedRoute requiredPermission="CRUD Ubicacion Inventario"><UbicacionInventarioManagement createMode={true} /></ProtectedRoute>} />
          <Route path="/categoria-inventario" element={<ProtectedRoute requiredPermission="CRUD Categoria Inventario"><CategoriaInventarioManagement /></ProtectedRoute>} />
          <Route path="/inventory/category" element={<ProtectedRoute requiredPermission="CRUD Categoria Inventario"><CategoriaInventarioManagement /></ProtectedRoute>} />
          <Route path="/inventory/category/create" element={<ProtectedRoute requiredPermission="CRUD Categoria Inventario"><CategoriaInventarioManagement createMode={true} /></ProtectedRoute>} />

          <Route path="/partners" element={<ProtectedRoute requiredPermission="CRUD Socias"><PartnersList /></ProtectedRoute>} />
          <Route path="/register-partner" element={<ProtectedRoute requiredPermission="CRUD Socias"><RegisterPartner /></ProtectedRoute>} />
          <Route path="/register-partner/:id" element={<ProtectedRoute requiredPermission="CRUD Socias"><EditPartner /></ProtectedRoute>} />
          <Route path="/register-partner/summary" element={<ProtectedRoute requiredPermission="CRUD Socias"><RegisterPartnerSummary /></ProtectedRoute>} />

          {/* Formatos (proteger con permiso CRUD Formatos) */}
          <Route path="/editar-formatos" element={<ProtectedRoute requiredPermission="CRUD Formatos"><EditarFormatos /></ProtectedRoute>} />

          <Route path="/requisitos-socia" element={<ProtectedRoute requiredPermission="CRUD Requisitos"><RequisitosSociaManagement /></ProtectedRoute>} />

          <Route path="/tratamientos" element={<ProtectedRoute requiredPermission="CRUD Tratamientos"><TratamientoManagement /></ProtectedRoute>} />
          <Route path="/tratamientos/create" element={<ProtectedRoute requiredPermission="CRUD Tratamientos"><TratamientoManagement createMode={true} /></ProtectedRoute>} />

          <Route path="/reports" element={<ProtectedRoute requiredPermission="CRUD Reportes"><Reports /></ProtectedRoute>} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AppContent />
    </Router>
  );
}

export default App;